import React, { useState } from "react";
import API from "../utils/API";
import "./rulemakingTasks.css";

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

function todayPT() {
    return new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
}

function formatDue(value) {
    return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * The Workspace tasks on one rulemaking item, with a form to add another.
 *
 * item:   { ref, title, url, dueDate } — ref identifies the item ("notice:Z2026-0921-01"); dueDate
 *         ("YYYY-MM-DD", optional) pre-fills the due date.
 * teams:  the Workspaces the user can create tasks in: [{ team_id, team_name, can_assign, members: [{ user_id, name }] }]
 * tasks:  rulemaking tasks for those Workspaces (any item; this component picks out the item's own)
 * Changes are reported through onCreated(task), onUpdated(task) and onDeleted(task) so the parent keeps the list.
 */
export default function RulemakingTasks({ item, teams, tasks, userId, onCreated, onUpdated, onDeleted }) {
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [teamId, setTeamId] = useState(teams[0]?.team_id);
    const [text, setText] = useState("");
    const [assignee, setAssignee] = useState("");
    const [due, setDue] = useState(item.dueDate || "");
    const [priority, setPriority] = useState("");

    const today = todayPT();
    const itemTasks = tasks.filter(t => t.rulemaking_ref === item.ref);
    const teamById = new Map(teams.map(t => [t.team_id, t]));
    const team = teamById.get(teamId) || teams[0];
    const assignable = team.can_assign ? team.members : team.members.filter(m => m.user_id === userId);

    const openForm = () => {
        setAdding(true);
        setError("");
        setDue(item.dueDate && item.dueDate >= today ? item.dueDate : "");
    };

    const closeForm = () => {
        setAdding(false);
        setText("");
        setAssignee("");
        setPriority("");
        setError("");
    };

    const changeTeam = id => {
        setTeamId(id);
        setAssignee("");
    };

    const addTask = async () => {
        if (!text.trim() || saving) return;
        setSaving(true);
        setError("");
        try {
            const res = await API.createRulemakingTask(team.team_id, {
                task_text: text.trim(),
                assigned_to_user_id: assignee || null,
                due_date: due || null,
                priority: priority || null,
                created_by_user_id: userId,
                rulemaking_ref: item.ref,
                rulemaking_title: item.title,
                rulemaking_url: item.url || null,
            });
            onCreated(res.data);
            closeForm();
        } catch (err) {
            setError(err.response?.data?.message || "Could not add the task.");
        } finally {
            setSaving(false);
        }
    };

    const toggleTask = async task => {
        try {
            const res = await API.updateRulemakingTask(task.team_id, task.id, { completed: !task.completed, requesting_user_id: userId });
            onUpdated(res.data);
        } catch (err) {
            setError(err.response?.data?.message || "Could not update the task.");
        }
    };

    const removeTask = async task => {
        try {
            await API.deleteRulemakingTask(task.team_id, task.id, userId);
            onDeleted(task);
        } catch (err) {
            setError(err.response?.data?.message || "Could not delete the task.");
        }
    };

    return (
        <div className="rmt">
            <div className="rmt-head">
                <span className="rmt-title"><i className="bi bi-check2-square"></i>Workspace tasks{itemTasks.length > 0 ? ` (${itemTasks.length})` : ""}</span>
                {!adding && (
                    <button type="button" className="rmt-add-btn" onClick={openForm}>
                        <i className="bi bi-plus-lg"></i> Add task
                    </button>
                )}
            </div>

            {itemTasks.length > 0 && (
                <ul className="rmt-list">
                    {itemTasks.map(task => {
                        const taskTeam = teamById.get(task.team_id);
                        const overdue = task.due_date && !task.completed && task.due_date < today;
                        const canDelete = taskTeam?.can_assign || task.created_by_user_id === userId;
                        return (
                            <li key={task.id} className={`rmt-task${task.completed ? " rmt-task--done" : ""}`}>
                                <input type="checkbox" checked={task.completed} onChange={() => toggleTask(task)} aria-label="Mark task complete" />
                                <span className="rmt-task-text">{task.task_text}</span>
                                <span className="rmt-task-meta">
                                    {teams.length > 1 && taskTeam && <span className="rmt-chip">{taskTeam.team_name}</span>}
                                    {task.assignee_name && <span><i className="bi bi-person"></i>{task.assignee_name}</span>}
                                    {task.due_date && (
                                        <span className={overdue ? "rmt-overdue" : ""}><i className="bi bi-calendar-event"></i>{formatDue(task.due_date)}</span>
                                    )}
                                    {task.priority && <span className={`rmt-priority rmt-priority--${task.priority.toLowerCase()}`}>{task.priority}</span>}
                                </span>
                                {canDelete && (
                                    <button type="button" className="rmt-remove" onClick={() => removeTask(task)} title="Delete task">
                                        <i className="bi bi-trash3"></i>
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}

            {itemTasks.length === 0 && !adding && <p className="rmt-empty">No tasks yet for this item.</p>}

            {adding && (
                <div className="rmt-form">
                    <input
                        className="rmt-input rmt-text"
                        placeholder="New task, e.g. draft a comment letter"
                        value={text}
                        autoFocus
                        maxLength={500}
                        onChange={e => setText(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") addTask(); }}
                    />
                    <div className="rmt-form-row">
                        {teams.length > 1 && (
                            <select className="rmt-input rmt-select" value={team.team_id} onChange={e => changeTeam(Number(e.target.value))} aria-label="Workspace">
                                {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.team_name}</option>)}
                            </select>
                        )}
                        <select className="rmt-input rmt-select" value={assignee} onChange={e => setAssignee(e.target.value)} aria-label="Assign to">
                            <option value="">Unassigned</option>
                            {assignable.map(m => <option key={m.user_id} value={m.user_id}>{m.user_id === userId ? `${m.name} (me)` : m.name}</option>)}
                        </select>
                        <input type="date" className="rmt-input" value={due} onChange={e => setDue(e.target.value)} aria-label="Due date" />
                        <select className="rmt-input rmt-select" value={priority} onChange={e => setPriority(e.target.value)} aria-label="Priority">
                            <option value="">Priority</option>
                            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div className="rmt-form-actions">
                        <button type="button" className="rmt-btn rmt-btn--primary" disabled={!text.trim() || saving} onClick={addTask}>
                            {saving ? "Adding…" : "Add task"}
                        </button>
                        <button type="button" className="rmt-btn" onClick={closeForm}>Cancel</button>
                    </div>
                </div>
            )}

            {error && <p className="rmt-error">{error}</p>}
        </div>
    );
}
